import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { cartApi } from './cartAPI'
import { apiService } from '@/services/api'
import { buildEndpoint, getCustomerId } from '@/utils/apiUtils'
import { refreshCsCartItems } from '../csFlow/csCartSlice'
import { clearDiscount } from '../discount/discountSlice'

// Flutter-style Cart Items Controller - exact match
interface CartItemsState {
  // Reactive cart items list - initialized as empty list
  cartItems: Array<Record<string, any>>
  cartInfo: Record<string, any> | null
  isLoading: boolean
  isAddingItem: boolean // Loading عند إضافة منتج
  isClearingCart: boolean // Loading عند مسح السلة
  isUpdatingPrices: boolean // Loading عند تحديث الأسعار
  isUpdatingQuantity: boolean // Loading عند تحديث الكمية
  updatingItemId: string | null // ID العنصر الذي يتم تحديثه حالياً
  isDeletingItem: boolean // Loading عند حذف منتج
  deletingItemId: string | null // ID العنصر الذي يتم حذفه حالياً
  error: string | null
  isInitialized: boolean
  lastClearTime: string | null // وقت آخر مسح للسلة لمنع إعادة التحميل التلقائي
  isCalculating: boolean // منع إعادة حساب السعر المتكرر
  wasClearedAfterOrder: boolean // Flag to prevent refetching after order submission
  recentlyDeletedItems: Record<string, number> // Track recently deleted item IDs with timestamps to filter from API responses
}

const initialState: CartItemsState = {
  cartItems: [],
  cartInfo: null,
  isLoading: false,
  isAddingItem: false,
  isClearingCart: false,
  isUpdatingPrices: false,
  isUpdatingQuantity: false,
  updatingItemId: null,
  isDeletingItem: false,
  deletingItemId: null,
  error: null,
  isInitialized: false,
  lastClearTime: null,
  isCalculating: false,
  wasClearedAfterOrder: false,
  recentlyDeletedItems: {}, // Track recently deleted items: { itemId: timestamp }
}

// Helper to resolve customer ID for CS operations (Prefers Redux first, falls back to persisted storage)
function resolveCsCustomerId(state: RootState): number | null {
  const stateCustomerId = state.csFlow?.customerId || state.customer?.customerId || null
  if (stateCustomerId && stateCustomerId > 0) {
    return stateCustomerId
  }
  return getCustomerId()
}

// Flutter-style async thunks matching exact controller methods
export const loadCartItems = createAsyncThunk(
  'cartItems/loadCartItems',
  async (params: { forceRefresh?: boolean; couponCode?: string; tableId?: number }, { getState, dispatch }) => {
    try {
      const state = getState() as RootState

      // التحقق من نوع الحساب - منع الاستدعاء للـ CS
      if (isCustomerServiceAccount(state)) {
        // للـ CS، لا نحتاج لتحميل عناصر السلة من هذا الـ controller
        return { cartItems: [], cartInfo: null }
      }

      // CRITICAL: If cart was cleared after order submission, don't fetch from API
      // This prevents old data from reappearing in UI
      if (state.cartItems.wasClearedAfterOrder && !params.forceRefresh) {
        // Cart was cleared, return empty state
        return { cartItems: [], cartInfo: null }
      }

      // حماية محسنة: إذا كان آخر تحديث كان منذ أقل من ثانية واحدة، تجاهل الاستدعاء
      if (!params.forceRefresh) {
        const lastUpdated = state.cartItems.cartInfo?.last_updated
        if (lastUpdated) {
          try {
            const lastUpdateTime = new Date(lastUpdated)
            const timeSinceLastUpdate = Date.now() - lastUpdateTime.getTime()
            if (timeSinceLastUpdate < 2000) {
              return { cartItems: state.cartItems.cartItems, cartInfo: state.cartItems.cartInfo }
            }
          } catch (e) {
            // تجاهل خطأ parsing التاريخ
          }
        }
      }

      // استخدام API مع تضمين الكوبون والطاولة إن وُجدا
      const userId = getCorrectUserId(state)

      // Get couponCode from params, or fallback to state sources
      let couponCode = params.couponCode
      if (!couponCode || !couponCode.trim()) {
        couponCode = state.cart?.discount_code || state.discount?.discountCode || undefined
      }

      // Get tableId from params, or fallback to state sources
      let tableId = params.tableId
      if (!tableId || tableId === 0) {
        // Try to get from cartSlice first
        if (state.cart?.table_id) {
          const cartTableId = typeof state.cart.table_id === 'number'
            ? state.cart.table_id
            : Number(state.cart.table_id)
          if (!isNaN(cartTableId) && cartTableId > 0) {
            tableId = cartTableId
          }
        }

        // If still not found, try cartInfo
        if ((!tableId || tableId === 0) && state.cartItems.cartInfo?.table_id) {
          const cartInfoTableId = typeof state.cartItems.cartInfo.table_id === 'number'
            ? state.cartItems.cartInfo.table_id
            : Number(state.cartItems.cartInfo.table_id)
          if (!isNaN(cartInfoTableId) && cartInfoTableId > 0) {
            tableId = cartInfoTableId
          }
        }

        // If still not found, try tablesSlice
        if ((!tableId || tableId === 0) && state.tables?.selectedTable) {
          const selectedTableId = Number(state.tables.selectedTable)
          if (!isNaN(selectedTableId) && selectedTableId > 0) {
            tableId = selectedTableId
          }
        }
      }

      const response = await dispatch(cartApi.endpoints.getCartData.initiate({
        userId,
        couponCode,
        tableId,
      }))

      if (response.data && typeof response.data === 'object') {
        const dataField = response.data as Record<string, any>

        // Update cart info with proper discount handling
        const cartInfo: Record<string, any> = {
          id: dataField.id?.toString() ?? '',
          user_id: dataField.user_id?.toString() ?? '',
          status: dataField.status?.toString() ?? '',
          branch_id: dataField.branch_id?.toString() ?? '',
          platform: dataField.platform?.toString() ?? '',
          address_id: dataField.address_id?.toString() ?? '',
          type_id: dataField.type_id?.toString() ?? '',
          tax_percentage: dataField.tax_percentage?.toString() ?? '15',
          delivery_price: dataField.delivery_price?.toString() ?? '0',
          table_price: dataField.table_price?.toString() ?? '0',
          service_price: dataField.service_price?.toString() ?? '0',
          price: dataField.price?.toString() ?? '0',
          tax_value: dataField.tax_value?.toString() ?? '0',
          sub_total: dataField.sub_total?.toString() ?? '0',
          total_price: dataField.total_price?.toString() ?? '0',
          discount: dataField.discount?.toString() ?? '0', // Ensure discount is properly updated
          coupon_code: dataField.coupon_code?.toString() ?? dataField.coboneCode?.toString() ?? '',
          allowed_payment_methods: dataField.allowed_payment_methods ?? {},
        }
        // Include items_count if provided by API
        if (dataField.items_count != null) {
          cartInfo.items_count = dataField.items_count.toString()
        }

        // Update cart items
        let cartItems: Array<Record<string, any>> = []
        if (dataField.items) {
          cartItems = dataField.items
            .filter((item: any) => item != null)
            .map((item: any) => processRawCartItem(item))
            .filter((item: Record<string, any> | null) =>
              item != null &&
              Object.keys(item).length > 0 &&
              item.id?.toString().length > 0
            ) as Array<Record<string, any>>

          // Filter out recently deleted items to prevent them from reappearing
          cartItems = filterDeletedItems(cartItems, state.cartItems.recentlyDeletedItems)
        }

        // Calculate items_count from cartItems if not provided by API
        if (!cartInfo.items_count && cartItems.length > 0) {
          const totalItems = cartItems.reduce((sum, item) => {
            const quantity = parseInt(item.quantity?.toString() ?? '1') || 1
            return sum + quantity
          }, 0)
          cartInfo.items_count = totalItems.toString()
        } else if (!cartInfo.items_count) {
          cartInfo.items_count = '0'
        }

        return { cartItems, cartInfo }
      }

      return { cartItems: [], cartInfo: null }
    } catch (error) {
      throw error
    }
  }
)

export const refreshCartItems = createAsyncThunk(
  'cartItems/refreshCartItems',
  async (params: { couponCode?: string; tableId?: number; forceRefresh?: boolean }, { getState, dispatch }) => {
    try {
      const state = getState() as RootState

      // التحقق من نوع الحساب - منع الاستدعاء للـ CS
      if (isCustomerServiceAccount(state)) {
        return { cartItems: [], cartInfo: createEmptyCartInfo() }
      }

      // CRITICAL: If cart was cleared after order submission, don't fetch from API
      // This prevents old data from reappearing in UI
      if (state.cartItems.wasClearedAfterOrder && !params.forceRefresh) {
        return { cartItems: [], cartInfo: createEmptyCartInfo() }
      }

      // Check if we have a token
      const token = apiService.getAuthToken()
      if (!token || token.length === 0) {
        return { cartItems: [], cartInfo: createEmptyCartInfo() }
      }

      // Refresh from API including coupon and table if present
      const userId = getCorrectUserId(state)

      // Get couponCode from params, or fallback to state sources
      let couponCode = params.couponCode
      if (!couponCode || !couponCode.trim()) {
        couponCode = state.cart?.discount_code || state.discount?.discountCode || undefined
      }

      // Get tableId from params, or fallback to state sources
      let tableId = params.tableId
      if (!tableId || tableId === 0) {
        // Try to get from cartSlice first
        if (state.cart?.table_id) {
          const cartTableId = typeof state.cart.table_id === 'number'
            ? state.cart.table_id
            : Number(state.cart.table_id)
          if (!isNaN(cartTableId) && cartTableId > 0) {
            tableId = cartTableId
          }
        }

        // If still not found, try cartInfo
        if ((!tableId || tableId === 0) && state.cartItems.cartInfo?.table_id) {
          const cartInfoTableId = typeof state.cartItems.cartInfo.table_id === 'number'
            ? state.cartItems.cartInfo.table_id
            : Number(state.cartItems.cartInfo.table_id)
          if (!isNaN(cartInfoTableId) && cartInfoTableId > 0) {
            tableId = cartInfoTableId
          }
        }

        // If still not found, try tablesSlice
        if ((!tableId || tableId === 0) && state.tables?.selectedTable) {
          const selectedTableId = Number(state.tables.selectedTable)
          if (!isNaN(selectedTableId) && selectedTableId > 0) {
            tableId = selectedTableId
          }
        }
      }

      // Use direct API call to bypass RTK Query cache and get fresh data immediately
      let url = buildEndpoint(`/cashier/carts/user/${userId}`, userId)
      const urlParams = new URLSearchParams()

      if (couponCode && couponCode.trim()) {
        urlParams.append('coboneCode', couponCode)
      }
      if (tableId && tableId > 0) {
        urlParams.append('tableId', tableId.toString())
      }

      if (urlParams.toString()) {
        url += `?${urlParams.toString()}`
      }

      const fullUrl = `${apiService.axios.defaults.baseURL}${url}`
      const apiResponse = await apiService.axios.get(url)

      // Unwrap response similar to RTK Query transformResponse
      let responseData = apiResponse.data
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        responseData = responseData.data
      }

      const response = { data: responseData }

      // إذا ظهر خطأ Cart Not Found، امسح البيانات محلياً ولا نحاول مرة أخرى
      if (response.data && typeof response.data === 'object' && 'message' in response.data && 'descriptiveMessage' in response.data && response.data.message === 'error' && response.data.descriptiveMessage === 'Cart Not Found') {
        return { cartItems: [], cartInfo: createEmptyCartInfo() }
      }

      if (response.data && typeof response.data === 'object') {
        const dataField = response.data as Record<string, any>

        // Update cart info with proper discount handling
        const cartInfo: Record<string, any> = {
          id: dataField.id?.toString() ?? '',
          user_id: dataField.user_id?.toString() ?? '',
          branch_id: dataField.branch_id?.toString() ?? '',
          platform: dataField.platform?.toString() ?? '',
          address_id: dataField.address_id?.toString() ?? '',
          type_id: dataField.type_id?.toString() ?? '',
          tax_percentage: dataField.tax_percentage?.toString() ?? '15',
          delivery_price: dataField.delivery_price?.toString() ?? '0',
          table_price: dataField.table_price?.toString() ?? '0',
          service_price: dataField.service_price?.toString() ?? '0',
          price: dataField.price?.toString() ?? '0',
          tax_value: dataField.tax_value?.toString() ?? '0',
          sub_total: dataField.sub_total?.toString() ?? '0',
          total_price: dataField.total_price?.toString() ?? '0',
          discount: dataField.discount?.toString() ?? '0', // Ensure discount is properly updated
          coupon_code: dataField.coupon_code?.toString() ?? dataField.coboneCode?.toString() ?? '',
          allowed_payment_methods: dataField.allowed_payment_methods ?? {},
          last_updated: new Date().toISOString(), // تحديث timestamp لضمان تحديث الواجهة
        }
        // Include items_count if provided by API
        if (dataField.items_count != null) {
          cartInfo.items_count = dataField.items_count.toString()
        }

        // Update cart items with preservation of additionals
        let cartItems: Array<Record<string, any>> = []
        if (dataField.items) {
          cartItems = dataField.items.map((item: any) => {
            // Process the item with safety
            const processedItem = ensureItemSafety(item)

            // Preserve additionals from existing item if available
            if (processedItem.id) {
              const itemId = processedItem.id.toString()
              const existingItem = getCartItemById(state, itemId)

              if (existingItem &&
                existingItem.additionals &&
                Array.isArray(existingItem.additionals) &&
                existingItem.additionals.length > 0) {
                // Preserve existing additionals
                processedItem.additionals = existingItem.additionals
              }
            }

            return processedItem
          })

          // Filter out recently deleted items to prevent them from reappearing
          cartItems = filterDeletedItems(cartItems, state.cartItems.recentlyDeletedItems)
        }

        // Calculate items_count from cartItems if not provided by API
        if (!cartInfo.items_count && cartItems.length > 0) {
          const totalItems = cartItems.reduce((sum, item) => {
            const quantity = parseInt(item.quantity?.toString() ?? '1') || 1
            return sum + quantity
          }, 0)
          cartInfo.items_count = totalItems.toString()
        } else if (!cartInfo.items_count) {
          cartInfo.items_count = '0'
        }

        return { cartItems, cartInfo }
      }

      // Return empty cart with proper cartInfo structure instead of null
      return { cartItems: [], cartInfo: createEmptyCartInfo() }
    } catch (error) {
      // Handle 401 error by clearing cart locally
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.status === 401) {
          return { cartItems: [], cartInfo: createEmptyCartInfo() }
        }
      }
      throw error
    }
  }
)

// Helper function to create empty cartInfo with proper structure
function createEmptyCartInfo(): Record<string, any> {
  return {
    id: '',
    user_id: '',
    branch_id: '',
    platform: '',
    address_id: '',
    type_id: '',
    tax_percentage: '15',
    delivery_price: '0',
    table_price: '0',
    price: '0',
    tax_value: '0',
    sub_total: '0',
    total_price: '0',
    discount: '0',
    coupon_code: '',
    items_count: '0',
    allowed_payment_methods: {},
    last_updated: new Date().toISOString(),
  }
}

// Recalculate cart totals based on the currently filtered items to avoid showing stale API totals.
function updateCartInfoTotalsFromItems(
  cartInfo: Record<string, any>,
  filteredItems: Array<Record<string, any>>
) {
  if (!cartInfo) {
    return
  }

  if (filteredItems.length === 0) {
    cartInfo.sub_total = '0'
    cartInfo.tax_value = '0'
    cartInfo.total_price = '0'
    cartInfo.items_count = '0'
    cartInfo.last_updated = new Date().toISOString()
    return
  }

  let subTotal = 0.0
  let totalItems = 0

  for (const item of filteredItems) {
    const itemPrice = parseFloat(item.total_price?.toString() ?? item.total_unit_price?.toString() ?? '0') || 0
    const itemQuantity = parseInt(item.quantity?.toString() ?? '1') || 1
    subTotal += itemPrice
    totalItems += itemQuantity
  }

  const taxPercentage = parseFloat(cartInfo.tax_percentage?.toString() ?? '15') || 15
  const taxValue = subTotal * (taxPercentage / 100)
  const tablePrice = parseFloat(cartInfo.table_price?.toString() ?? '0') || 0
  const deliveryPrice = parseFloat(cartInfo.delivery_price?.toString() ?? '0') || 0
  const discountAmount = parseFloat(cartInfo.discount?.toString() ?? '0') || 0
  const totalPrice = Math.max(0, subTotal + taxValue + tablePrice + deliveryPrice - discountAmount)

  cartInfo.sub_total = subTotal.toString()
  cartInfo.tax_value = taxValue.toString()
  cartInfo.total_price = totalPrice.toString()
  cartInfo.items_count = totalItems.toString()
  cartInfo.last_updated = new Date().toISOString()
}

// Comprehensive ID normalization function that handles all format variations
// This ensures consistent ID comparison across the application
function normalizeItemId(itemId: string | number | null | undefined): string {
  // Handle null/undefined
  if (itemId == null) {
    return ''
  }

  // Convert to string and trim whitespace
  let normalized = String(itemId).trim()

  // Return empty string if result is empty
  if (normalized.length === 0) {
    return ''
  }

  // Remove "i" prefix if present (e.g., "i93" -> "93")
  // Only remove if it's a single 'i' followed by at least one character
  if (normalized.startsWith('i') && normalized.length > 1) {
    normalized = normalized.substring(1)
  }

  // Remove any leading/trailing whitespace again after prefix removal
  normalized = normalized.trim()

  return normalized
}

// Helper function to clean itemId (remove "i" prefix if present)
// Kept for backward compatibility, but now uses normalizeItemId internally
function cleanItemId(itemId: string | number): string {
  return normalizeItemId(itemId)
}

// Generate all possible ID variations for comprehensive matching
function getAllIdVariations(itemId: string | number | null | undefined): Set<string> {
  const variations = new Set<string>()
  const normalized = normalizeItemId(itemId)

  if (!normalized || normalized.length === 0) {
    return variations
  }

  // Add normalized form
  variations.add(normalized)

  // Add with "i" prefix
  variations.add(`i${normalized}`)

  // Add original if different
  const original = String(itemId || '').trim()
  if (original && original !== normalized && original !== `i${normalized}`) {
    variations.add(original)
  }

  // Add numeric form if it's a valid number
  const numericId = parseInt(normalized, 10)
  if (!isNaN(numericId) && numericId.toString() === normalized) {
    variations.add(numericId.toString())
    variations.add(`i${numericId}`)
  }

  return variations
}

function buildDeletedItemIdSet(recentlyDeletedItems: Record<string, number>): Set<string> {
  const now = Date.now()
  const deletedItemIds = new Set<string>()

  for (const [itemId, timestamp] of Object.entries(recentlyDeletedItems)) {
    // Only consider items deleted within the last 60 seconds
    if (now - timestamp < 60000) {
      // Get all possible variations of this deleted item ID
      const variations = getAllIdVariations(itemId)
      variations.forEach(variation => {
        if (variation && variation.length > 0) {
          deletedItemIds.add(variation)
        }
      })
    }
  }

  return deletedItemIds
}

// Helper function to filter out recently deleted items from cart items
// Load permanently deleted items from localStorage
function loadPermanentlyDeletedItems(): Record<string, number> {
  try {
    const deletedItemsKey = 'cart_permanently_deleted_items'
    const storedDeleted = localStorage.getItem(deletedItemsKey)
    if (!storedDeleted) return {}

    const deletedItems: Record<string, number> = JSON.parse(storedDeleted)

    // Clean up old items (older than 24 hours)
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000)
    const cleanedDeletedItems: Record<string, number> = {}
    for (const [key, value] of Object.entries(deletedItems)) {
      if (value > oneDayAgo) {
        cleanedDeletedItems[key] = value
      }
    }

    // Update localStorage with cleaned items
    if (Object.keys(cleanedDeletedItems).length !== Object.keys(deletedItems).length) {
      localStorage.setItem(deletedItemsKey, JSON.stringify(cleanedDeletedItems))
    }

    return cleanedDeletedItems
  } catch (error) {
    
    return {}
  }
}

// Enhanced to check all possible ID format variations
// Also checks localStorage for permanently deleted items
function filterDeletedItems(
  items: Array<Record<string, any>>,
  recentlyDeletedItems: Record<string, number>
): Array<Record<string, any>> {
  // Merge state deleted items with localStorage deleted items
  const permanentDeletedItems = loadPermanentlyDeletedItems()
  const allDeletedItems = { ...recentlyDeletedItems, ...permanentDeletedItems }

  const deletedItemIds = buildDeletedItemIdSet(allDeletedItems)

  if (deletedItemIds.size === 0) {
    return items
  }

  return items.filter((item) => {
    // Get all possible variations of this item's ID
    const itemIdVariations = getAllIdVariations(item.id)

    // Check if any variation matches a deleted item ID
    for (const variation of itemIdVariations) {
      if (deletedItemIds.has(variation)) {
        // This item matches a deleted item, filter it out
        return false
      }
    }

    // Also check alternative ID fields (item_id, product_id) for comprehensive matching
    if (item.item_id != null) {
      const itemIdVariations2 = getAllIdVariations(item.item_id)
      for (const variation of itemIdVariations2) {
        if (deletedItemIds.has(variation)) {
          return false
        }
      }
    }

    if (item.product_id != null) {
      const itemIdVariations3 = getAllIdVariations(item.product_id)
      for (const variation of itemIdVariations3) {
        if (deletedItemIds.has(variation)) {
          return false
        }
      }
    }

    // Item is not in deleted list, keep it
    return true
  })
}

// Improved cleanup function that only removes entries after confirming item is truly gone
// More conservative approach: only remove if item is confirmed absent from server response
function cleanupRecentlyDeletedItems(
  recentlyDeletedItems: Record<string, number>,
  serverItems: Array<Record<string, any>> | undefined
) {
  if (!serverItems || serverItems.length === 0) {
    // If server returns empty items, we can be more aggressive in cleanup
    // But still keep recently deleted items for a short time to prevent reappearing
    const now = Date.now()
    const keysToKeep: string[] = []

    for (const [key, timestamp] of Object.entries(recentlyDeletedItems)) {
      // Keep items deleted within last 30 seconds even if server is empty
      // This prevents race conditions where server hasn't fully processed deletion
      if (now - timestamp < 30000) {
        keysToKeep.push(key)
      }
    }

    // Only keep recent deletions
    const cleaned: Record<string, number> = {}
    for (const key of keysToKeep) {
      cleaned[key] = recentlyDeletedItems[key]
    }

    // Clear the object and repopulate
    Object.keys(recentlyDeletedItems).forEach(key => delete recentlyDeletedItems[key])
    Object.assign(recentlyDeletedItems, cleaned)
    return
  }

  // Build comprehensive set of all ID variations present in server response
  const serverIdVariations = new Set<string>()
  for (const item of serverItems) {
    // Get all variations for item.id
    if (item.id != null) {
      const variations = getAllIdVariations(item.id)
      variations.forEach(v => serverIdVariations.add(v))
    }

    // Also check alternative ID fields
    if (item.item_id != null) {
      const variations = getAllIdVariations(item.item_id)
      variations.forEach(v => serverIdVariations.add(v))
    }

    if (item.product_id != null) {
      const variations = getAllIdVariations(item.product_id)
      variations.forEach(v => serverIdVariations.add(v))
    }
  }

  // Only remove entries if the deleted item ID (and all its variations) are NOT in server response
  // This is conservative: if we're not 100% sure the item is gone, keep tracking it
  const keysToRemove: string[] = []
  const now = Date.now()

  for (const [deletedKey, timestamp] of Object.entries(recentlyDeletedItems)) {
    // Get all variations of the deleted item ID
    const deletedVariations = getAllIdVariations(deletedKey)

    // Check if ANY variation appears in server response
    let foundInServer = false
    for (const variation of deletedVariations) {
      if (serverIdVariations.has(variation)) {
        foundInServer = true
        break
      }
    }

    // Only remove if:
    // 1. Item is confirmed NOT in server response, AND
    // 2. At least 5 seconds have passed since deletion (to avoid race conditions)
    if (!foundInServer && (now - timestamp) >= 5000) {
      keysToRemove.push(deletedKey)
    }
  }

  // Remove confirmed absent items
  for (const key of keysToRemove) {
    delete recentlyDeletedItems[key]
  }
}

function clearDiscountAndTableIfCartEmpty(dispatch: any, getState: () => unknown) {
  try {
    const state = getState() as RootState
    if (state.cartItems.cartItems.length === 0) {
      dispatch(clearDiscount())

      if (state.tables?.selectedTable && state.tables.selectedTable > 0) {
        dispatch({ type: 'tables/clearTable' })
      }
    }
  } catch (error) {
    // Ignore cleanup failures
  }
}

export const updateCartItemQuantity = createAsyncThunk(
  'cartItems/updateCartItemQuantity',
  async (params: { itemId: string; quantity: number }, { getState, dispatch }) => {
    try {
      // Validate quantity
      if (!params.quantity || params.quantity < 1) {
        return { success: false, error: 'Invalid quantity. Quantity must be at least 1.' }
      }

      // Clean and validate itemId
      const cleanedItemId = cleanItemId(params.itemId)
      if (!cleanedItemId || cleanedItemId === '0') {
        return { success: false, error: 'Invalid item ID' }
      }

      const state = getState() as RootState

      // التحقق من نوع المستخدم قبل محاولة تحديث العنصر من API
      const employeeType = getEmployeeType(state)
      if (employeeType === 'customer_service') {
        // للـ CS، احصل على customer ID (Redux أو من التخزين المحلي كنسخة احتياطية)
        const customerId = resolveCsCustomerId(state)

        if (!customerId || customerId === 0) {
          // لا يوجد customer ID
          return { success: false, error: 'No customer selected' }
        }

        // Build endpoint using cart ID (CS uses cart-specific routes)
        const csCartId = state.csCart.cartInfo?.id?.toString() ?? ''
        if (!csCartId) {
          return { success: false, error: 'Customer service cart ID not found' }
        }
        const endpoint = buildEndpoint(`/cashier/carts/${csCartId}/item/${cleanedItemId}/quantity`)

        // Make API call to update cart item quantity for CS
        // Endpoint: PUT /customerService/carts/user/{customerId}/item/{itemId}
        try {
          const response = await apiService.axios.put(
            endpoint,
            { quantity: params.quantity }
          )

          // Accept 200, 201, or 204 as success
          if (response.status === 200 || response.status === 201 || response.status === 204) {
            // بعد التحديث الناجح، قم بتحديث السلة من API بشكل فوري
            // Force refresh the cart from API - this is critical!
            const refreshResult = await dispatch(refreshCsCartItems({ customerId, forceRefresh: true }) as any)

            if (refreshResult.type === 'csCart/refreshCsCartItems/rejected') {
              // Throw error to indicate refresh failed
              throw new Error('Failed to refresh cart after quantity update')
            }

            return { success: true }
          } else {
            return { success: false, error: `API returned status ${response.status}` }
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update quantity'
          const errorStatus = error.response?.status

          // Provide more specific error messages
          if (errorStatus === 404) {
            return { success: false, error: `Cart item not found (itemId: ${cleanedItemId})` }
          } else if (errorStatus === 400) {
            return { success: false, error: errorMessage || 'Invalid request. Please check the item ID and quantity.' }
          } else if (errorStatus === 401 || errorStatus === 403) {
            return { success: false, error: 'Authentication failed. Please login again.' }
          }

          return { success: false, error: errorMessage }
        }
      }

      // Get cart ID for API call
      const cartId = state.cartItems.cartInfo?.id?.toString() ?? ''

      if (!cartId || cartId.length === 0) {
        return { success: false, error: 'Cart ID not found. Please try again.' }
      }

      // Update quantity locally first for immediate UI feedback
      dispatch(updateItemQuantityLocally({
        itemId: params.itemId,
        newQuantity: params.quantity
      }))

      // Build endpoint using the correct format: /cashier/carts/{cartId}/item/{itemId}/quantity
      const endpoint = buildEndpoint(`/cashier/carts/${cartId}/item/${cleanedItemId}/quantity`)

      // Make API call to update cart item quantity using /cashier/carts/{cartId}/item/{itemId}/quantity
      // Endpoint: PUT /cashier/carts/{cartId}/item/{itemId}/quantity
      // IMPORTANT: API uses PUT method for quantity updates
      const response = await apiService.axios.put(
        endpoint,
        { quantity: params.quantity }
      )

      if (response.status === 200 || response.status === 201 || response.status === 204) {
        // بعد التحديث الناجح، قم بتحديث السلة من API بشكل فوري باستخدام API call مباشر
        const userId = getCorrectUserId(state)
        const couponCode = state.cart.discount_code || state.discount?.discountCode || undefined
        // Get tableId from all possible sources
        let tableId: number | undefined = undefined

        // 1. Try cartSlice
        if (state.cart?.table_id) {
          const cartTableId = typeof state.cart.table_id === 'number'
            ? state.cart.table_id
            : Number(state.cart.table_id)
          if (!isNaN(cartTableId) && cartTableId > 0) {
            tableId = cartTableId
          }
        }

        // 2. Try cartInfo
        if ((!tableId || tableId === 0) && state.cartItems.cartInfo?.table_id) {
          const cartInfoTableId = typeof state.cartItems.cartInfo.table_id === 'number'
            ? state.cartItems.cartInfo.table_id
            : Number(state.cartItems.cartInfo.table_id)
          if (!isNaN(cartInfoTableId) && cartInfoTableId > 0) {
            tableId = cartInfoTableId
          }
        }

        // 3. Try tablesSlice
        if ((!tableId || tableId === 0) && state.tables?.selectedTable) {
          const selectedTableId = Number(state.tables.selectedTable)
          if (!isNaN(selectedTableId) && selectedTableId > 0) {
            tableId = selectedTableId
          }
        }

        // استدعاء API مباشر لتحديث السلة: GET /cashier/carts/user/{userId}
        try {
          // Build URL with query parameters
          let url = buildEndpoint(`/cashier/carts/user/${userId}`, userId)
          const urlParams = new URLSearchParams()

          if (couponCode && couponCode.trim()) {
            urlParams.append('coboneCode', couponCode)
          }
          if (tableId && tableId > 0) {
            urlParams.append('tableId', tableId.toString())
          }

          if (urlParams.toString()) {
            url += `?${urlParams.toString()}`
          }

          // Make direct API call
          const apiResponse = await apiService.axios.get(url)

          // Unwrap response
          let responseData = apiResponse.data
          if (responseData && typeof responseData === 'object' && 'data' in responseData) {
            responseData = responseData.data
          }

          // Handle Cart Not Found error
          if (responseData && typeof responseData === 'object' && 'message' in responseData && 'descriptiveMessage' in responseData && responseData.message === 'error' && responseData.descriptiveMessage === 'Cart Not Found') {
            return { success: true } // Still return success as quantity was updated
          }

          // Update cart data from API response
          if (responseData && typeof responseData === 'object') {
            const dataField = responseData as Record<string, any>

            // Update cart info
            const cartInfo: Record<string, any> = {
              id: dataField.id?.toString() ?? '',
              user_id: dataField.user_id?.toString() ?? '',
              branch_id: dataField.branch_id?.toString() ?? '',
              platform: dataField.platform?.toString() ?? '',
              address_id: dataField.address_id?.toString() ?? '',
              type_id: dataField.type_id?.toString() ?? '',
              tax_percentage: dataField.tax_percentage?.toString() ?? '15',
              delivery_price: dataField.delivery_price?.toString() ?? '0',
              table_price: dataField.table_price?.toString() ?? '0',
              service_price: dataField.service_price?.toString() ?? '0',
              price: dataField.price?.toString() ?? '0',
              tax_value: dataField.tax_value?.toString() ?? '0',
              sub_total: dataField.sub_total?.toString() ?? '0',
              total_price: dataField.total_price?.toString() ?? '0',
              discount: dataField.discount?.toString() ?? '0',
              allowed_payment_methods: dataField.allowed_payment_methods ?? {},
              last_updated: new Date().toISOString(),
            }
            // Include items_count if provided by API
            if (dataField.items_count != null) {
              cartInfo.items_count = dataField.items_count.toString()
            }

            // Update cart items
            let cartItems: Array<Record<string, any>> = []
            if (dataField.items) {
              cartItems = dataField.items
                .filter((item: any) => item != null)
                .map((item: any) => {
                  const processedItem = processRawCartItem(item)
                  // Preserve additionals from existing item if available
                  if (processedItem && processedItem.id) {
                    const itemId = processedItem.id.toString()
                    const existingItem = getCartItemById(state, itemId)
                    if (existingItem &&
                      existingItem.additionals &&
                      Array.isArray(existingItem.additionals) &&
                      existingItem.additionals.length > 0) {
                      processedItem.additionals = existingItem.additionals
                    }
                  }
                  return processedItem
                })
                .filter((item: Record<string, any> | null) =>
                  item != null &&
                  Object.keys(item).length > 0 &&
                  item.id?.toString().length > 0
                ) as Array<Record<string, any>>

              // Filter out recently deleted items
              cartItems = filterDeletedItems(cartItems, state.cartItems.recentlyDeletedItems)
            }

            // Calculate items_count from cartItems if not provided by API
            if (!cartInfo.items_count && cartItems.length > 0) {
              const totalItems = cartItems.reduce((sum, item) => {
                const quantity = parseInt(item.quantity?.toString() ?? '1') || 1
                return sum + quantity
              }, 0)
              cartInfo.items_count = totalItems.toString()
            } else if (!cartInfo.items_count) {
              cartInfo.items_count = '0'
            }

            // Update state directly using dispatch
            // Format: { items: [...], ...cartInfo }
            // Note: updateCartDataFromApi will also filter, but we filter here too for safety
            const cartDataToUpdate = {
              items: cartItems,
              ...cartInfo
            }
            dispatch(updateCartDataFromApi(cartDataToUpdate))
          }
        } catch (refreshError: any) {
          // حتى في حالة الخطأ، نعيد success لأن التحديث تم على الخادم
        }

        return { success: true }
      } else {
        return { success: false, error: `API returned status ${response.status}` }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update quantity'
      const errorStatus = error.response?.status

      // Provide more specific error messages
      if (errorStatus === 404) {
        return { success: false, error: `Cart item not found (itemId: ${cleanItemId(params.itemId)})` }
      } else if (errorStatus === 400) {
        return { success: false, error: errorMessage || 'Invalid request. Please check the item ID and quantity.' }
      } else if (errorStatus === 401 || errorStatus === 403) {
        return { success: false, error: 'Authentication failed. Please login again.' }
      }

      return { success: false, error: errorMessage }
    }
  }
)

export const removeCartItemFromApi = createAsyncThunk(
  'cartItems/removeCartItemFromApi',
  async (itemId: string, { getState, dispatch }) => {
    try {
      const state = getState() as RootState

      // التحقق من نوع المستخدم قبل محاولة حذف العنصر من API
      const employeeType = getEmployeeType(state)
      if (employeeType === 'customer_service') {
        // للـ CS، احصل على customer ID (Redux أو من التخزين المحلي كنسخة احتياطية)
        const customerId = resolveCsCustomerId(state)

        if (!customerId || customerId === 0) {
          // لا يوجد customer ID
          return { success: false, error: 'No customer selected' }
        }

        // Clean itemId (remove "i" prefix if present)
        const cleanedItemId = cleanItemId(itemId)
        if (!cleanedItemId || cleanedItemId === '0') {
          return { success: false, error: 'Invalid item ID' }
        }

        // Build endpoint using cart ID (CS uses cart-specific routes)
        const csCartId = state.csCart.cartInfo?.id?.toString() ?? ''
        if (!csCartId) {
          return { success: false, error: 'Customer service cart ID not found' }
        }
        const endpoint = buildEndpoint(`/cashier/carts/${csCartId}/item/${cleanedItemId}`)

        // Remove item locally first for immediate UI feedback
        dispatch(removeItemLocally(itemId))

        // Make API call to delete cart item for CS
        // Endpoint: DELETE /customerService/carts/user/{customerId}/item/{itemId}
        try {
          const response = await apiService.axios.delete(endpoint)

          // Accept 200 or 204 as success
          if (response.status === 200 || response.status === 204) {
            // بعد الحذف الناجح، قم بتحديث السلة من API بشكل فوري
            // Force refresh the cart from API - this is critical!
            const refreshResult = await dispatch(refreshCsCartItems({ customerId, forceRefresh: true }) as any)

            if (refreshResult.type === 'csCart/refreshCsCartItems/rejected') {
              // Throw error to indicate refresh failed
              throw new Error('Failed to refresh cart after item deletion')
            }

            return { success: true }
          } else {
            return { success: false, error: `API returned status ${response.status}` }
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to delete item'
          const errorStatus = error.response?.status

          // Provide more specific error messages
          if (errorStatus === 404) {
            return { success: false, error: `Cart item not found (itemId: ${cleanedItemId})` }
          } else if (errorStatus === 400) {
            return { success: false, error: errorMessage || 'Invalid request. Please check the item ID.' }
          } else if (errorStatus === 401 || errorStatus === 403) {
            return { success: false, error: 'Authentication failed. Please login again.' }
          }

          return { success: false, error: errorMessage }
        }
      }

      // Check if we have a token
      const token = apiService.getAuthToken()
      if (!token || token.length === 0) {
        // لا يوجد token، احذف محلياً فقط
        dispatch(removeItemLocally(itemId))
        return { success: true }
      }

      // Check if we have cart ID
      const cartId = state.cartItems.cartInfo?.id?.toString() ?? ''
      if (cartId.length === 0) {
        // لا يوجد cart ID، احذف محلياً فقط
        dispatch(removeItemLocally(itemId))
        return { success: true }
      }

      // Clean itemId (remove "i" prefix if present)
      const cleanedItemId = cleanItemId(itemId)

      // Make API call to delete cart item using cart ID and item ID
      // Endpoint: DELETE /cashier/carts/{cartId}/item/{itemId}
      const response = await apiService.axios.delete(buildEndpoint(`/cashier/carts/${cartId}/item/${cleanedItemId}`))

      if (response.status === 200 || response.status === 204) {
        // بعد الحذف الناجح، قم بمسح جميع البيانات القديمة بشكل كامل
        // Track the deleted item first to prevent it from reappearing - PERMANENTLY
        const timestamp = Date.now()
        const allVariations = getAllIdVariations(itemId)
        const allCleanedVariations = getAllIdVariations(cleanedItemId)
        const allDeletedVariations = new Set([...allVariations, ...allCleanedVariations])

        // Permanently store deleted item in localStorage to survive page refreshes
        try {
          const deletedItemsKey = 'cart_permanently_deleted_items'
          const storedDeleted = localStorage.getItem(deletedItemsKey)
          const deletedItems: Record<string, number> = storedDeleted ? JSON.parse(storedDeleted) : {}

          // Store all variations of the deleted item with timestamp
          for (const variation of allDeletedVariations) {
            if (variation && variation.length > 0) {
              deletedItems[variation] = timestamp
            }
          }

          // Keep only items deleted in the last 24 hours to prevent localStorage bloat
          const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000)
          const cleanedDeletedItems: Record<string, number> = {}
          for (const [key, value] of Object.entries(deletedItems)) {
            if (value > oneDayAgo) {
              cleanedDeletedItems[key] = value
            }
          }

          localStorage.setItem(deletedItemsKey, JSON.stringify(cleanedDeletedItems))
        } catch (storageError) {
          // If localStorage fails, continue without it
       
        }

        // Track deleted item by dispatching removeItemLocally which handles tracking
        // This ensures the item is tracked in recentlyDeletedItems before we clear the cart
        dispatch(removeItemLocally(itemId))
        if (cleanedItemId !== itemId) {
          dispatch(removeItemLocally(cleanedItemId))
        }

        // Invalidate RTK Query cache to ensure fresh data
        try {
          dispatch(cartApi.util.invalidateTags(['Cart']))
        } catch (cacheError) {
          // If cache invalidation fails, continue anyway
          
        }

        // الحصول على couponCode و tableId من state قبل المسح
        const couponCode = state.cart.discount_code || state.discount?.discountCode || undefined
        // الحصول على tableId من state.cart.table_id وتحويله إلى number
        // Get tableId from all possible sources
        let tableId: number | undefined = undefined

        // 1. Try cartSlice
        if (state.cart?.table_id) {
          const cartTableId = typeof state.cart.table_id === 'number'
            ? state.cart.table_id
            : Number(state.cart.table_id)
          if (!isNaN(cartTableId) && cartTableId > 0) {
            tableId = cartTableId
          }
        }

        // 2. Try cartInfo
        if ((!tableId || tableId === 0) && state.cartItems.cartInfo?.table_id) {
          const cartInfoTableId = typeof state.cartItems.cartInfo.table_id === 'number'
            ? state.cartItems.cartInfo.table_id
            : Number(state.cartItems.cartInfo.table_id)
          if (!isNaN(cartInfoTableId) && cartInfoTableId > 0) {
            tableId = cartInfoTableId
          }
        }

        // 3. Try tablesSlice
        if ((!tableId || tableId === 0) && state.tables?.selectedTable) {
          const selectedTableId = Number(state.tables.selectedTable)
          if (!isNaN(selectedTableId) && selectedTableId > 0) {
            tableId = selectedTableId
          }
        }

        // COMPLETELY reset local cart data - clear everything
        // This ensures no stale data remains
        dispatch({ type: 'cartItems/resetCartForRefetch' })

        // استدعاء refreshCartItems لإعادة جلب السلة من API مع forceRefresh: true لضمان الحصول على البيانات المحدثة
        try {
          const refreshResult = await dispatch(refreshCartItems({ couponCode, tableId, forceRefresh: true }) as any)
          if (refreshResult.type === 'cartItems/refreshCartItems/fulfilled') {
            // Enhanced verification: Check all possible ID variations
            const refreshedItems = refreshResult.payload?.cartItems || []
            const refreshedCartInfo = refreshResult.payload?.cartInfo

            // Verify that cartInfo was properly updated
            if (!refreshedCartInfo) {
              // If cartInfo is missing, log warning but continue
              
            }

            // Check if any item in refreshed cart matches any variation of deleted item
            const itemStillExists = refreshedItems.some((item: any) => {
              const itemVariations = getAllIdVariations(item.id)

              // Check if any variation matches
              for (const itemVar of itemVariations) {
                if (allDeletedVariations.has(itemVar)) {
                  return true
                }
              }

              // Also check alternative ID fields
              if (item.item_id != null) {
                const itemIdVariations = getAllIdVariations(item.item_id)
                for (const itemVar of itemIdVariations) {
                  if (allDeletedVariations.has(itemVar)) {
                    return true
                  }
                }
              }

              if (item.product_id != null) {
                const productIdVariations = getAllIdVariations(item.product_id)
                for (const itemVar of productIdVariations) {
                  if (allDeletedVariations.has(itemVar)) {
                    return true
                  }
                }
              }

              return false
            })

            if (itemStillExists) {
              // Item still exists in refreshed cart - remove it immediately and permanently
              // This handles edge cases where the filter might have missed it
              dispatch(removeItemLocally(itemId))

              // Also remove with cleanedItemId to ensure both formats are tracked
              if (cleanedItemId !== itemId) {
                dispatch(removeItemLocally(cleanedItemId))
              }

              // Force another refetch to ensure the item is gone
              await dispatch(refreshCartItems({ couponCode, tableId, forceRefresh: true }) as any)
            }
          } else if (refreshResult.type === 'cartItems/refreshCartItems/rejected') {
            // If refresh failed, we should still return success since deletion succeeded
            // But log the error for debugging
       
          }
        } catch (refreshError) {
        
          // The recentlyDeletedItems tracking will still prevent reappearance
          
        }
        return { success: true }
      } else {
  
        return { success: false }
      }
    } catch (error) {
   
      dispatch(removeItemLocally(itemId))
      return { success: false }
    } finally {
      clearDiscountAndTableIfCartEmpty(dispatch, getState)
    }
  }
)

export const clearCartItemsFromApi = createAsyncThunk(
  'cartItems/clearCartItemsFromApi',
  async (_, { getState }) => {
    try {
      const state = getState() as RootState

      // التحقق من نوع المستخدم قبل محاولة مسح السلة من API
      const employeeType = getEmployeeType(state)
      if (employeeType === 'customer_service') {
        return true
      }

      // Check if we have a token
      const token = apiService.getAuthToken()
      if (!token || token.length === 0) {
        return true
      }

      // Check if we have cart ID
      const cartId = state.cartItems.cartInfo?.id?.toString() ?? ''
      if (cartId.length === 0) {
        return true
      }

      // Make API call to clear cart using cart ID
      const response = await apiService.axios.delete(buildEndpoint(`/cashier/carts/${cartId}`))

      if (response.status === 200 || response.status === 204) {
        return true
      } else {
        return false
      }
    } catch (error) {
      // في حالة الخطأ، امسح البيانات محلياً
      return true // نعتبر العملية ناجحة لأن البيانات تم مسحها محلياً
    }
  }
)

// Flutter-style Cart Items Slice - exact match
const cartItemsSlice = createSlice({
  name: 'cartItems',
  initialState,
  reducers: {
    // Flutter-style reducers matching exact controller methods
    initializeCartItems: (state) => {
      // Initialize with safe defaults
      state.cartItems = []
      state.cartInfo = null
      state.isLoading = false
      state.error = null

      // Load permanently deleted items from localStorage and merge with state
      const permanentDeletedItems = loadPermanentlyDeletedItems()
      state.recentlyDeletedItems = { ...state.recentlyDeletedItems, ...permanentDeletedItems }

      state.isInitialized = true
    },

    addItemLocally: (state, action: PayloadAction<Record<string, any>>) => {
      try {
        const item = action.payload
        // Reset flag when adding items (user is starting a new order)
        state.wasClearedAfterOrder = false

        // التحقق من وجود المنتج في السلة باستخدام معرف فريد
        const uniqueId = item.id?.toString() ?? ''

        // التحقق من وجود المنتج باستخدام المعرف الفريد
        if (uniqueId.length > 0) {
          const existingItemIndex = state.cartItems.findIndex(
            (cartItem) => cartItem.id?.toString() === uniqueId
          )

          if (existingItemIndex !== -1) {
            // إذا كان المنتج موجود، لا تضيفه مرة أخرى
            return
          }
        }

        // التحقق من وجود المنتج باستخدام itemId أيضاً لمنع الإضافات المزدوجة
        const itemId = item.itemId
        if (itemId != null) {
          const existingItemByItemId = state.cartItems.findIndex(
            (cartItem) => cartItem.itemId?.toString() === itemId.toString()
          )

          if (existingItemByItemId !== -1) {
            // إذا كان المنتج موجود بنفس itemId، لا تضيفه مرة أخرى
            return
          }
        }

        // إذا كان المنتج جديد، أضفه للسلة
        // تأكد من أن المنتج يحتوي على جميع البيانات المطلوبة
        const cartItem = { ...item }

        // إضافة معرف السلة إذا لم يكن موجوداً
        if (cartItem.cart_id == null) {
          cartItem.cart_id = '1'
        }

        // تأكد من أن المعرف فريد
        if (cartItem.id == null || cartItem.id.toString().length === 0) {
          cartItem.id = `local_${Date.now()}_${cartItem.itemId ?? 'unknown'}`
        }

        // تأكد من أن الاسم ليس فارغاً
        if (cartItem.name == null ||
          cartItem.name.toString().length === 0 ||
          cartItem.name.toString() === 'Unknown Item') {
          // حاول الحصول على الاسم من meal_info
          if (cartItem.meal_info != null && typeof cartItem.meal_info === 'object') {
            const mealInfo = cartItem.meal_info as Record<string, any>
            cartItem.name = mealInfo.mealName?.toString() ?? 'Product'
          } else {
            cartItem.name = 'Product'
          }
        }

        // تأكد من أن السعر ليس فارغاً
        if (cartItem.price == null || cartItem.price.toString().length === 0) {
          cartItem.price = '0.00'
        }

        // تأكد من أن السعر الإجمالي ليس فارغاً
        if (cartItem.total_price == null ||
          cartItem.total_price.toString().length === 0) {
          const price = parseFloat(cartItem.price?.toString() ?? '0') || 0
          const quantity = parseInt(cartItem.quantity?.toString() ?? '1') || 1
          cartItem.total_price = (price * quantity).toString()
        }

        // تأكد من وجود الإضافات
        if (cartItem.additionals == null || !Array.isArray(cartItem.additionals)) {
          cartItem.additionals = []
        }

        // تأكد من وجود extras
        if (cartItem.extras == null || !Array.isArray(cartItem.extras)) {
          cartItem.extras = []
        }

        // تأكد من أن المنتج محلي
        cartItem.isLocal = true

        state.cartItems.push(cartItem)
      } catch (e) {
        // Ignore errors
      }
    },

    removeItemLocally: (state, action: PayloadAction<string>) => {
      try {
        const itemId = action.payload
        const initialCount = state.cartItems.length
        const timestamp = Date.now()

        // Track all possible ID variations to ensure comprehensive filtering
        // This prevents items from reappearing due to ID format inconsistencies
        const allVariations = getAllIdVariations(itemId)
        for (const variation of allVariations) {
          if (variation && variation.length > 0) {
            state.recentlyDeletedItems[variation] = timestamp
          }
        }

        // Filter out the item using all possible ID variations
        state.cartItems = state.cartItems.filter((item) => {
          const itemVariations = getAllIdVariations(item.id)
          // Check if any variation of the item matches any variation of the deleted item
          for (const itemVar of itemVariations) {
            if (allVariations.has(itemVar)) {
              return false // Item matches deleted item, filter it out
            }
          }
          return true // Item doesn't match, keep it
        })
        const finalCount = state.cartItems.length

        if (finalCount < initialCount) {
          // إعادة حساب الأسعار محلياً فوراً بناءً على المنتجات المتبقية
          let subTotal = 0.0
          let totalItems = 0

          for (const item of state.cartItems) {
            const itemPrice = parseFloat(item.total_price?.toString() ?? item.total_unit_price?.toString() ?? '0') || 0
            const itemQuantity = parseInt(item.quantity?.toString() ?? '1') || 1
            subTotal += itemPrice
            totalItems += itemQuantity
          }

          // تحديث معلومات السلة مع حساب كامل للأسعار محلياً
          if (state.cartItems.length === 0) {
            // إذا كانت السلة فارغة تماماً، تعيين جميع القيم إلى صفر
            if (state.cartInfo) {
              state.cartInfo.sub_total = '0'
              state.cartInfo.tax_value = '0'
              state.cartInfo.total_price = '0'
              state.cartInfo.items_count = '0'
              state.cartInfo.last_updated = new Date().toISOString()
            } else {
              // إنشاء cartInfo جديد بقيم صفر
              state.cartInfo = {
                sub_total: '0',
                tax_value: '0',
                total_price: '0',
                items_count: '0',
                tax_percentage: '15',
                table_price: '0',
                delivery_price: '0',
                discount: '0',
                last_updated: new Date().toISOString()
              }
            }
          } else if (state.cartInfo) {
            // إذا كانت السلة تحتوي على عناصر، حساب الأسعار
            const taxPercentage = parseFloat(state.cartInfo.tax_percentage?.toString() ?? '15') || 15.0
            const taxValue = subTotal * (taxPercentage / 100)
            const tablePrice = parseFloat(state.cartInfo.table_price?.toString() ?? '0') || 0.0
            const deliveryPrice = parseFloat(state.cartInfo.delivery_price?.toString() ?? '0') || 0.0
            const discountAmount = parseFloat(state.cartInfo.discount?.toString() ?? '0') || 0.0
            const totalPrice = subTotal + taxValue + tablePrice + deliveryPrice - discountAmount

            // تحديث جميع القيم محلياً لضمان دقة العرض الفوري
            state.cartInfo.sub_total = subTotal.toString()
            state.cartInfo.tax_value = taxValue.toString()
            state.cartInfo.total_price = totalPrice.toString()
            state.cartInfo.items_count = totalItems.toString()
            state.cartInfo.last_updated = new Date().toISOString()
          }
        }
      } catch (e) {
        // Ignore errors
      }
    },

    updateItemQuantityLocally: (state, action: PayloadAction<{ itemId: string; newQuantity: number; newTotalPrice?: string }>) => {
      try {
        const { itemId, newQuantity, newTotalPrice } = action.payload

        // البحث عن العنصر في cartItems وتحديث كميته وسعره الإجمالي
        for (const item of state.cartItems) {
          if (item.id?.toString() === itemId) {
            item.quantity = newQuantity.toString()

            // تحديث السعر الإجمالي إذا تم توفيره، أو احسبه من السعر الوحدة
            if (newTotalPrice) {
              item.total_price = newTotalPrice
              item.total_unit_price = newTotalPrice
            } else {
              // احسب السعر الإجمالي من السعر الوحدة × الكمية
              const unitPrice = parseFloat(item.unit_price || item.price || '0')
              const calculatedTotal = (unitPrice * newQuantity).toString()
              item.total_price = calculatedTotal
              item.total_unit_price = calculatedTotal
            }
            break
          }
        }

        // إعادة حساب الأسعار الإجمالية للسلة فوراً
        let subTotal = 0.0
        let totalItems = 0

        for (const item of state.cartItems) {
          const itemPrice = parseFloat(item.total_price?.toString() ?? item.total_unit_price?.toString() ?? '0') || 0
          const itemQuantity = parseInt(item.quantity?.toString() ?? '1') || 1
          subTotal += itemPrice
          totalItems += itemQuantity
        }

        // تحديث معلومات السلة - استخدام البيانات من API فقط، بدون حسابات محلية
        if (state.cartInfo) {
          // فقط تحديث sub_total و items_count، الضريبة والإجمالي من API
          state.cartInfo.sub_total = subTotal.toString()
          state.cartInfo.items_count = totalItems.toString()
          state.cartInfo.last_updated = new Date().toISOString()
          // tax_value و total_price سيتم تحديثهما من API عند refreshCartItems
        }
      } catch (e) {
        // Ignore errors
      }
    },

    recalculatePricesImmediately: (state) => {
      try {
        // منع إعادة الحساب المتكرر
        if (state.isCalculating) {
          return
        }

        // تفعيل حالة تحديث الأسعار فوراً
        state.isUpdatingPrices = true
        state.isCalculating = true

        // حساب سريع جداً بدون تأخير
        let subTotal = 0.0
        let totalItems = 0

        // حساب المجموع الفرعي وعدد المنتجات من المنتجات الموجودة حالياً
        for (const item of state.cartItems) {
          const itemPrice = parseFloat(item.total_price?.toString() ?? '0') || 0
          const itemQuantity = parseInt(item.quantity?.toString() ?? '1') || 1

          subTotal += itemPrice
          totalItems += itemQuantity
        }

        // استخدام البيانات من API فقط، بدون حسابات محلية للضريبة
        // الحفاظ على القيم الحالية من API
        const existingTaxValue = state.cartInfo?.tax_value?.toString() ?? '0'
        const existingTotalPrice = state.cartInfo?.total_price?.toString() ?? '0'
        const existingDiscount = state.cartInfo?.discount?.toString() ?? '0'
        const existingTaxPercentage = state.cartInfo?.tax_percentage?.toString() ?? '15'

        // تحديث معلومات السلة - فقط sub_total و items_count، الباقي من API
        if (state.cartInfo) {
          state.cartInfo.price = subTotal.toString()
          state.cartInfo.sub_total = subTotal.toString()
          state.cartInfo.items_count = totalItems.toString()
          state.cartInfo.last_updated = new Date().toISOString()
          // tax_value و total_price من API، لا نحسبها محلياً
        } else {
          state.cartInfo = {
            id: Date.now().toString(),
            user_id: '7',
            branch_id: '1',
            platform: 'cashier',
            address_id: '1',
            type_id: '2',
            tax_percentage: existingTaxPercentage,
            delivery_price: '0',
            price: subTotal.toString(),
            tax_value: existingTaxValue,
            sub_total: subTotal.toString(),
            total_price: existingTotalPrice,
            discount: existingDiscount,
            allowed_payment_methods: {},
            items_count: totalItems.toString(),
            last_updated: new Date().toISOString(),
          }
        }

        state.isCalculating = false
        state.isUpdatingPrices = false
      } catch (e) {
        state.isCalculating = false
        state.isUpdatingPrices = false
      }
    },

    clearCartLocally: (state) => {
      // Clear all data
      state.cartItems = []
      state.cartInfo = {
        sub_total: '0',
        tax_value: '0',
        total_price: '0',
        discount: '0',
        items_count: '0',
        tax_percentage: '15',
        delivery_price: '0',
        table_price: '0',
        type_id: '0',
      }
      state.error = null
      state.isClearingCart = false
      state.lastClearTime = new Date().toISOString()
      // Clear recently deleted items when cart is cleared
      state.recentlyDeletedItems = {}
    },

    resetCartForRefetch: (state) => {
      // Reset cart items and info for refetch, but preserve recentlyDeletedItems
      // This is used when deleting items to ensure deleted items don't reappear after refetch
      state.cartItems = []
      // Set cartInfo to null so it gets properly reinitialized from API response
      state.cartInfo = null
      state.error = null
      // Note: We do NOT clear recentlyDeletedItems here - it's preserved to filter out deleted items
      // Note: We do NOT set wasClearedAfterOrder - we want to allow refetching
    },

    clearCartItemsImmediately: (state) => {
      // تسجيل وقت المسح لمنع إعادة التحميل التلقائي
      state.lastClearTime = new Date().toISOString()

      // مسح جميع البيانات
      state.cartItems = []
      state.cartInfo = {
        sub_total: '0',
        tax_value: '0',
        total_price: '0',
        discount: '0',
        items_count: '0',
        tax_percentage: '15',
        delivery_price: '0',
        table_price: '0',
        type_id: '0',
      }
      state.error = null
      state.isClearingCart = true // الحفاظ على حالة المسح لمنع إعادة التحميل
      // Set flag to prevent refetching after order submission
      state.wasClearedAfterOrder = true
      // Clear recently deleted items when cart is cleared
      state.recentlyDeletedItems = {}
    },

    // Clean old deleted items (older than 60 seconds)
    cleanOldDeletedItems: (state) => {
      const now = Date.now()
      const cleaned: Record<string, number> = {}
      for (const [itemId, timestamp] of Object.entries(state.recentlyDeletedItems)) {
        if (now - timestamp < 60000) { // Keep items deleted within last 60 seconds
          cleaned[itemId] = timestamp
        }
      }
      state.recentlyDeletedItems = cleaned
    },

    stopClearingCart: (state) => {
      state.isClearingCart = false
    },

    showAddingItemLoading: (state) => {
      state.isAddingItem = true
    },

    hideAddingItemLoading: (state) => {
      state.isAddingItem = false
    },

    updateCartDataFromApi: (state, action: PayloadAction<Record<string, any>>) => {
      try {
        const cartData = action.payload
        // تحديث البيانات من API
        if (cartData.items) {
          // Filter out recently deleted items before updating state
          const rawItems = cartData.items.map((item: any) => ensureItemSafety(item))
          const filteredItems = filterDeletedItems(rawItems, state.recentlyDeletedItems)
          state.cartItems = filteredItems

          // Cleanup recently deleted items based on server response
          cleanupRecentlyDeletedItems(state.recentlyDeletedItems, rawItems)
        }

        // تحديث معلومات السلة من API
        state.cartInfo = { ...cartData }

        // Ensure table_id is preserved if not in API response
        if (!state.cartInfo.table_id) {
          // Try to get from cartSlice (will be set by setTableLocally)
          // Note: We can't access state.cart here, so we rely on the caller to ensure table_id is in cartData
          // The table_id should be included in the API response, but if not, it will be preserved from previous state
        }
      } catch (e) {
        // Ignore errors في التحديث
      }
    },

    updateCartDataFromCs: (state, action: PayloadAction<{ cartItems: Array<Record<string, any>>; totalPrice: number; itemsCount: number }>) => {
      try {
        const { cartItems, totalPrice, itemsCount } = action.payload
        // Filter out recently deleted items before updating state
        const filteredItems = filterDeletedItems(cartItems, state.recentlyDeletedItems)
        state.cartItems = filteredItems

        // تحديث معلومات السلة
        updateCartInfoFromCs(state, totalPrice, itemsCount)
      } catch (e) {
        // Ignore errors
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCartItems.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loadCartItems.fulfilled, (state, action) => {
        state.isLoading = false
        const rawCartItems = action.payload.cartItems || []
        const filteredItems = filterDeletedItems(rawCartItems, state.recentlyDeletedItems)
        state.cartItems = filteredItems
        const didFilterDeletedItems = rawCartItems.length !== filteredItems.length

        if (action.payload.cartInfo) {
          state.cartInfo = { ...action.payload.cartInfo }

          if (action.payload.cartInfo.table_id) {
            state.cartInfo.table_id = action.payload.cartInfo.table_id.toString()
          } else if (state.cartInfo?.table_id) {
            state.cartInfo.table_id = state.cartInfo.table_id.toString()
          }

          if (didFilterDeletedItems && state.cartInfo) {
            updateCartInfoTotalsFromItems(state.cartInfo, filteredItems)
          }
        } else {
          state.cartInfo = null
        }

        cleanupRecentlyDeletedItems(state.recentlyDeletedItems, rawCartItems)
        state.error = null
        // Clean old deleted items (older than 60 seconds)
        const now = Date.now()
        const cleaned: Record<string, number> = {}
        for (const [itemId, timestamp] of Object.entries(state.recentlyDeletedItems)) {
          if (now - timestamp < 60000) { // Keep items deleted within last 60 seconds
            cleaned[itemId] = timestamp
          }
        }
        state.recentlyDeletedItems = cleaned
        // Reset flag if items were actually loaded (not empty)
        // This allows normal cart operations after clearing
        if (filteredItems && filteredItems.length > 0) {
          state.wasClearedAfterOrder = false
        }
      })
      .addCase(loadCartItems.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to load cart items'
      })
      .addCase(refreshCartItems.pending, (_state) => {
        // Don't set loading for refresh to avoid UI disruption
      })
      .addCase(refreshCartItems.fulfilled, (state, action) => {
        const rawCartItems = action.payload.cartItems || []
        const filteredItems = filterDeletedItems(rawCartItems, state.recentlyDeletedItems)
        const didFilterDeletedItems = rawCartItems.length !== filteredItems.length

        // Post-refresh verification: Double-check that no deleted items slipped through
        // This is a safety net to catch any edge cases the filter might have missed
        const verifiedItems: Array<Record<string, any>> = []
        const deletedItemIds = buildDeletedItemIdSet(state.recentlyDeletedItems)

        for (const item of filteredItems) {
          let shouldKeep = true

          // Check all ID variations of this item against deleted items
          const itemVariations = getAllIdVariations(item.id)
          for (const itemVar of itemVariations) {
            if (deletedItemIds.has(itemVar)) {
              shouldKeep = false
              break
            }
          }

          // Also check alternative ID fields
          if (shouldKeep && item.item_id != null) {
            const itemIdVariations = getAllIdVariations(item.item_id)
            for (const itemVar of itemIdVariations) {
              if (deletedItemIds.has(itemVar)) {
                shouldKeep = false
                break
              }
            }
          }

          if (shouldKeep && item.product_id != null) {
            const productIdVariations = getAllIdVariations(item.product_id)
            for (const itemVar of productIdVariations) {
              if (deletedItemIds.has(itemVar)) {
                shouldKeep = false
                break
              }
            }
          }

          if (shouldKeep) {
            verifiedItems.push(item)
          }
        }

        // Force update by creating new references for all nested objects
        if (verifiedItems && Array.isArray(verifiedItems)) {
          state.cartItems = verifiedItems.map(item => ({ ...item }))
        } else {
          state.cartItems = []
        }

        if (action.payload.cartInfo) {
          state.cartInfo = { ...action.payload.cartInfo }

          if (action.payload.cartInfo.table_id) {
            state.cartInfo.table_id = action.payload.cartInfo.table_id.toString()
          } else if (state.cartInfo?.table_id) {
            state.cartInfo.table_id = state.cartInfo.table_id.toString()
          }

          // Only recalculate totals if items were filtered (deleted items removed)
          // Otherwise, use API totals as source of truth
          const finalDidFilter = rawCartItems.length !== verifiedItems.length
          if (finalDidFilter && state.cartInfo && verifiedItems.length > 0) {
            // Items were filtered, recalculate totals from remaining items
            updateCartInfoTotalsFromItems(state.cartInfo, verifiedItems)
          } else if (verifiedItems.length === 0 && state.cartInfo) {
            // No items left, set totals to zero
            updateCartInfoTotalsFromItems(state.cartInfo, verifiedItems)
          }
          // If no filtering occurred and items exist, keep API totals as-is
        } else {
          if (!state.cartInfo) {
            state.cartInfo = createEmptyCartInfo()
          }
          // If no cartInfo from API, calculate from items
          // Only recalculate if we didn't get fresh info from API
          if (!action.payload.cartInfo) {
            updateCartInfoTotalsFromItems(state.cartInfo, verifiedItems)
          }
        }

        cleanupRecentlyDeletedItems(state.recentlyDeletedItems, rawCartItems)
        state.error = null
        const now = Date.now()
        const cleaned: Record<string, number> = {}
        for (const [itemId, timestamp] of Object.entries(state.recentlyDeletedItems)) {
          if (now - timestamp < 60000) {
            cleaned[itemId] = timestamp
          }
        }
        state.recentlyDeletedItems = cleaned
        // Don't reset wasClearedAfterOrder flag here - it should only be reset in loadCartItems.fulfilled
        // This prevents deleted items from reappearing when navigating to home page
        // The flag is meant to prevent refetching after order submission, not after item deletion
      })
      .addCase(refreshCartItems.rejected, (_state, _action) => {
        // Don't update error state for quick refresh to avoid UI disruption
      })
      .addCase(updateCartItemQuantity.pending, (state, action) => {
        state.isUpdatingQuantity = true
        state.updatingItemId = action.meta.arg.itemId
        state.error = null
      })
      .addCase(updateCartItemQuantity.fulfilled, (state) => {
        state.isUpdatingQuantity = false
        state.updatingItemId = null
        // البيانات تم تحديثها بالفعل من loadCartItems
        state.error = null
      })
      .addCase(updateCartItemQuantity.rejected, (state, action) => {
        state.isUpdatingQuantity = false
        state.updatingItemId = null
        state.error = action.error.message || 'Failed to update cart item quantity'
      })
      .addCase(removeCartItemFromApi.pending, (state, action) => {
        // Set loading state for the item being deleted
        state.isDeletingItem = true
        state.deletingItemId = action.meta.arg
        state.error = null
      })
      .addCase(removeCartItemFromApi.fulfilled, (state, action) => {
        // Clear loading state after successful deletion
        // البيانات تم تحديثها بالفعل من refreshCartItems
        state.isDeletingItem = false
        state.deletingItemId = null
        state.error = null
      })
      .addCase(removeCartItemFromApi.rejected, (state, action) => {
        // Clear loading state even on error
        state.isDeletingItem = false
        state.deletingItemId = null
        state.error = action.error.message || 'Failed to remove cart item'
      })
      .addCase(clearCartItemsFromApi.pending, (state) => {
        state.isClearingCart = true
      })
      .addCase(clearCartItemsFromApi.fulfilled, (state) => {
        state.isClearingCart = false
        // Clear local data
        state.cartItems = []
        state.cartInfo = {
          sub_total: '0',
          tax_value: '0',
          total_price: '0',
          discount: '0',
          items_count: '0',
          tax_percentage: '15',
          delivery_price: '0',
          table_price: '0',
          type_id: '0',
        }
        state.error = null
        // Set flag to prevent refetching after order submission
        state.wasClearedAfterOrder = true
      })
      .addCase(clearCartItemsFromApi.rejected, (state) => {
        state.isClearingCart = false
        // في حالة الخطأ، امسح البيانات محلياً
        state.cartItems = []
        state.cartInfo = {
          sub_total: '0',
          tax_value: '0',
          total_price: '0',
          discount: '0',
          items_count: '0',
          tax_percentage: '15',
          delivery_price: '0',
          table_price: '0',
          type_id: '0',
        }
        state.error = null
      })
  },
})

// Flutter-style helper functions - exact match
function isCustomerServiceAccount(_state: RootState): boolean {
  void _state
  return isStoredUserTypeCustomerService(getStoredEmployeeUserType())
}

function getCorrectUserId(state: RootState): number {
  try {
    // Try to get customer ID first
    if (state.customer.hasCustomerData && state.customer.customerId) {
      return state.customer.customerId
    }
  } catch (e) {
    // Ignore error and proceed to cashier
  }

  // Fallback to cashier ID
  try {
    // First try to get from Redux state
    const userAny = state.auth.user as any
    if (userAny?.user_id) {
      return Number(userAny.user_id)
    }
    if (userAny?.user?.id) {
      return Number(userAny.user.id)
    }

    // Then try localStorage user_data
    const userDataStr = localStorage.getItem('user_data')
    if (userDataStr) {
      const userData = JSON.parse(userDataStr)
      if (userData.user_id) {
        return Number(userData.user_id)
      }
      if (userData.user?.id) {
        return Number(userData.user.id)
      }
    }

    // Finally try employee_data
    const employeeDataStr = localStorage.getItem('employee_data')
    if (employeeDataStr) {
      const employeeData = JSON.parse(employeeDataStr)
      if (employeeData.user_id) {
        return Number(employeeData.user_id)
      }
      if (employeeData.user?.id) {
        return Number(employeeData.user.id)
      }
    }
  } catch (e) {
    // Ignore error
  }

  return 7 // Default fallback
}

function getEmployeeType(_state: RootState): string {
  void _state
  return isStoredUserTypeCustomerService(getStoredEmployeeUserType()) ? 'customer_service' : 'cashier'
}

function parseStoredLocalData(key: string): any | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const storedValue = localStorage.getItem(key)
    if (!storedValue) return null
    return JSON.parse(storedValue)
  } catch (error) {
    return null
  }
}

function extractStoredUserType(data: any): string | null {
  if (!data || typeof data !== 'object') return null
  const candidates = [
    data.employee?.user_type,
    data.employee?.type,
    data.user?.user_type,
    data.user?.type,
    data.user_type,
    data.type,
  ]

  for (const candidate of candidates) {
    if (candidate == null) continue
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim()
      if (trimmed.length > 0) {
        return trimmed
      }
    } else {
      const strValue = String(candidate).trim()
      if (strValue.length > 0) {
        return strValue
      }
    }
  }

  return null
}

function getStoredEmployeeUserType(): string {
  const employeeData = parseStoredLocalData('employee_data')
  const employeeType = extractStoredUserType(employeeData)
  if (employeeType) {
    return employeeType
  }

  const userData = parseStoredLocalData('user_data')
  const userType = extractStoredUserType(userData)
  if (userType) {
    return userType
  }

  return ''
}

function isStoredUserTypeCustomerService(userType: string): boolean {
  if (!userType) return false

  const normalized = userType.toString().toLowerCase()
  if (normalized.includes('customer_service') || normalized.includes('customer service')) {
    return true
  }

  const tokens = normalized.split(/[\s_-]+/)
  return tokens.some((token) => token === 'cs')
}

function getCartItemById(state: RootState, itemId: string): Record<string, any> | null {
  try {
    if (itemId.length === 0) return null
    if (state.cartItems.cartItems.length === 0) return null

    return state.cartItems.cartItems.find(
      (item) => item.id?.toString() === itemId
    ) || null
  } catch (e) {
    return null
  }
}

function processRawCartItem(rawItem: any): Record<string, any> | null {
  try {
    if (!rawItem) return null

    const cartItem = { ...rawItem }

    // Ensure critical fields are never null
    cartItem.id = safeString(cartItem.id, '')
    cartItem.cart_id = safeString(cartItem.cart_id, '')
    cartItem.quantity = safeQuantity(cartItem.quantity)
    cartItem.unit_price = safePrice(cartItem.unit_price)
    cartItem.total_unit_price = safePrice(cartItem.total_unit_price)

    // Extract name and image from meals array with safety
    let itemName = 'Unknown Item'
    let imageUrl = ''
    let description = ''
    let calories = ''
    let size = ''
    let productName = ''
    let additionals: any[] = []

    try {
      if (cartItem.meals && Array.isArray(cartItem.meals)) {
        const meals = cartItem.meals
        if (meals.length > 0 && meals[0] != null) {
          const meal = meals[0]

          // Extract product name first (prioritize product name over meal name)
          if (meal.product && typeof meal.product === 'object') {
            const product = meal.product
            productName = safeString(product.name, '')
            if (productName.length > 0 && productName !== 'null') {
              itemName = productName // Use product name as primary name
            } else {
              itemName = safeString(meal.name, 'Unknown Item')
            }
          } else {
            itemName = safeString(meal.name, 'Unknown Item')
          }

          imageUrl = safeString(meal.image, '')
          description = safeString(meal.description, '')
          calories = safeString(meal.calories, '')
          size = safeString(meal.size, '')

          // Safe additionals extraction - preserve existing additionals if available
          if (meal.additionals && Array.isArray(meal.additionals)) {
            additionals = meal.additionals
          } else if (cartItem.additionals && Array.isArray(cartItem.additionals)) {
            // Fallback to existing additionals in cart item
            additionals = cartItem.additionals
          } else if (cartItem.extras && Array.isArray(cartItem.extras)) {
            // Additional fallback to extras field
            additionals = cartItem.extras
          } else if (cartItem.addons && Array.isArray(cartItem.addons)) {
            // Additional fallback to addons field
            additionals = cartItem.addons
          }
        }
      }
    } catch (e) {
      // Continue with defaults
    }

    // Set extracted values
    cartItem.name = itemName
    cartItem.image_url = imageUrl
    cartItem.description = description
    cartItem.calories = calories
    cartItem.size = size
    cartItem.product_name = productName
    cartItem.additionals = additionals

    // Ensure extras field exists
    if (!cartItem.extras || !Array.isArray(cartItem.extras)) {
      cartItem.extras = []
    }

    // Keep price for backward compatibility
    cartItem.price = cartItem.unit_price

    // Calculate total_price if not present
    if (!cartItem.total_price ||
      cartItem.total_price.toString().length === 0 ||
      cartItem.total_price.toString() === '0') {
      const price = parseFloat(cartItem.unit_price?.toString() ?? '0') || 0.0
      const quantity = parseInt(cartItem.quantity?.toString() ?? '1') || 1
      cartItem.total_price = (price * quantity).toString()
    }

    // Final safety check
    return ensureItemSafety(cartItem)
  } catch (e) {
    return null
  }
}

function ensureItemSafety(item: Record<string, any>): Record<string, any> {
  try {
    // Create a defensive copy
    const safeItem = { ...item }

    // Critical fields that must never be null or empty
    safeItem.id = safeString(safeItem.id, '')
    safeItem.cart_id = safeString(safeItem.cart_id, '')

    // Try to get name from meals array if name is empty or 'Unknown Item'
    let itemName = safeString(safeItem.name, '')
    if (itemName.length === 0 ||
      itemName === 'Unknown Item' ||
      itemName === 'null') {
      if (safeItem.meals && Array.isArray(safeItem.meals) && safeItem.meals.length > 0) {
        const meals = safeItem.meals
        if (meals[0] && typeof meals[0] === 'object') {
          const meal = meals[0]

          // Prioritize product name over meal name
          if (meal.product && typeof meal.product === 'object') {
            const product = meal.product
            const productName = safeString(product.name, '')
            if (productName.length > 0 && productName !== 'null') {
              itemName = productName
            } else {
              itemName = safeString(meal.name, '')
            }
          } else {
            itemName = safeString(meal.name, '')
          }
        }
      }
    }

    // If still no name, use a fallback
    if (itemName.length === 0 ||
      itemName === 'Unknown Item' ||
      itemName === 'null') {
      itemName = 'مكبوس لحم أربع قطع' // Fallback name
    }

    safeItem.name = itemName
    safeItem.quantity = safeQuantity(safeItem.quantity)
    safeItem.price = safePrice(safeItem.price)
    safeItem.unit_price = safePrice(safeItem.unit_price)
    safeItem.total_unit_price = safePrice(safeItem.total_unit_price)

    // Calculate total_price if not present or invalid
    if (!safeItem.total_price ||
      safeItem.total_price.toString().length === 0 ||
      safeItem.total_price.toString() === '0') {
      const price = parseFloat(safeItem.price?.toString() ?? '0') || 0.0
      const quantity = parseInt(safeItem.quantity?.toString() ?? '1') || 1
      safeItem.total_price = (price * quantity).toString()
    }

    // Optional fields with safe defaults
    safeItem.image_url = safeString(safeItem.image_url, '')
    safeItem.description = safeString(safeItem.description, '')
    safeItem.calories = safeString(safeItem.calories, '')
    safeItem.size = safeString(safeItem.size, '')
    safeItem.product_name = safeString(safeItem.product_name, '')

    // Ensure extras/additionals is always a list
    if (!safeItem.extras || !Array.isArray(safeItem.extras)) {
      safeItem.extras = []
    }
    if (!safeItem.additionals || !Array.isArray(safeItem.additionals)) {
      safeItem.additionals = []
    } else {
      // Validate and clean additionals data
      const additionals = safeItem.additionals
      const cleanedAdditionals = additionals.filter((additional: any) => {
        if (typeof additional === 'object' && additional !== null) {
          const name = additional.name?.toString() ?? ''
          const id = additional.id?.toString() ?? ''
          // Keep additionals that have either a valid name or a valid id
          return (name.length > 0 && name !== 'null') || (id.length > 0 && id !== 'null')
        }
        return false
      })

      safeItem.additionals = cleanedAdditionals
    }

    // Validate critical fields are not empty
    if (safeItem.id.toString().trim().length === 0) {
      safeItem.id = `fallback_${Date.now()}`
    }

    return safeItem
  } catch (e) {
    // Return a completely safe fallback item
    return createFallbackItem()
  }
}

function createFallbackItem(): Record<string, any> {
  return {
    id: `fallback_${Date.now()}`,
    cart_id: '',
    name: 'Unknown Item',
    quantity: '1',
    price: '0',
    unit_price: '0',
    total_unit_price: '0',
    total_price: '0',
    image_url: '',
    description: '',
    calories: '',
    size: '',
    product_name: '',
    extras: [],
    additionals: [],
    isLocal: true, // Mark as local item
  }
}

function safeString(value: any, fallback: string): string {
  try {
    if (value == null) return fallback
    const str = value.toString().trim()
    if (str.length === 0 || str === 'null' || str === 'undefined') return fallback
    return str
  } catch (e) {
    return fallback
  }
}

function safeQuantity(value: any): string {
  try {
    if (value == null) return '1'
    const quantity = parseInt(value.toString()) || 1
    return (quantity < 1 ? 1 : quantity).toString()
  } catch (e) {
    return '1'
  }
}

function safePrice(value: any): string {
  try {
    if (value == null) return '0'
    const price = parseFloat(value.toString()) || 0.0
    return (price < 0 ? 0.0 : price).toString()
  } catch (e) {
    return '0'
  }
}

function updateCartInfoFromCs(state: CartItemsState, totalPrice: number, itemsCount: number): void {
  try {
    // إذا كانت السلة فارغة، استخدم القيم الصفرية
    if (itemsCount === 0) {
      state.cartInfo = {
        id: state.cartInfo?.id ?? Date.now().toString(),
        user_id: state.cartInfo?.user_id ?? '7',
        branch_id: state.cartInfo?.branch_id ?? '1',
        platform: state.cartInfo?.platform ?? 'cs',
        address_id: state.cartInfo?.address_id ?? '1',
        type_id: state.cartInfo?.type_id ?? '1',
        tax_percentage: '15',
        delivery_price: state.cartInfo?.delivery_price ?? '0',
        service_price: state.cartInfo?.service_price ?? '0',
        price: '0',
        tax_value: '0',
        sub_total: '0',
        total_price: '0',
        discount: '0',
        allowed_payment_methods: state.cartInfo?.allowed_payment_methods ?? {},
        items_count: '0',
        last_updated: new Date().toISOString(),
      }
    } else {
      // Use existing tax values from API, don't calculate locally
      const existingTaxValue = state.cartInfo?.tax_value?.toString() ?? '0'
      const existingTaxPercentage = state.cartInfo?.tax_percentage?.toString() ?? '15'
      // Calculate sub_total from total_price if needed, but keep tax from API
      const subTotal = parseFloat(state.cartInfo?.sub_total?.toString() ?? '0') || 0

      state.cartInfo = {
        id: state.cartInfo?.id ?? Date.now().toString(),
        user_id: state.cartInfo?.user_id ?? '7',
        branch_id: state.cartInfo?.branch_id ?? '1',
        platform: state.cartInfo?.platform ?? 'cs',
        address_id: state.cartInfo?.address_id ?? '1',
        type_id: state.cartInfo?.type_id ?? '1',
        tax_percentage: existingTaxPercentage,
        delivery_price: state.cartInfo?.delivery_price ?? '0',
        service_price: state.cartInfo?.service_price ?? '0',
        price: subTotal.toString(),
        tax_value: existingTaxValue, // From API, not calculated
        sub_total: subTotal.toString(),
        total_price: totalPrice.toString(),
        discount: '0',
        allowed_payment_methods: state.cartInfo?.allowed_payment_methods ?? {},
        items_count: itemsCount.toString(),
        last_updated: new Date().toISOString(),
      }
    }
  } catch (e) {
    // Ignore errors
  }
}

// Flutter-style selectors - exact match
export const selectCartItems = (state: RootState) => state.cartItems.cartItems
export const selectCartInfo = (state: RootState) => state.cartItems.cartInfo
export const selectIsLoading = (state: RootState) => state.cartItems.isLoading
export const selectIsAddingItem = (state: RootState) => state.cartItems.isAddingItem
export const selectIsClearingCart = (state: RootState) => state.cartItems.isClearingCart
export const selectIsUpdatingPrices = (state: RootState) => state.cartItems.isUpdatingPrices
export const selectIsUpdatingQuantity = (state: RootState) => state.cartItems.isUpdatingQuantity
export const selectUpdatingItemId = (state: RootState) => state.cartItems.updatingItemId
export const selectIsDeletingItem = (state: RootState) => state.cartItems.isDeletingItem
export const selectDeletingItemId = (state: RootState) => state.cartItems.deletingItemId
export const selectError = (state: RootState) => state.cartItems.error
export const selectIsCartEmpty = (state: RootState) => state.cartItems.cartItems.length === 0
export const selectIsInitialized = (state: RootState) => state.cartItems.isInitialized
export const selectLastClearTime = (state: RootState) => state.cartItems.lastClearTime
export const selectTotalPrice = (state: RootState) => {
  try {
    return parseFloat(state.cartItems.cartInfo?.total_price?.toString() ?? '0') || 0.0
  } catch (e) {
    return 0.0
  }
}
export const selectSubTotal = (state: RootState) => {
  try {
    return parseFloat(state.cartItems.cartInfo?.sub_total?.toString() ?? '0') || 0.0
  } catch (e) {
    return 0.0
  }
}
export const selectTaxValue = (state: RootState) => {
  try {
    return parseFloat(state.cartItems.cartInfo?.tax_value?.toString() ?? '0') || 0.0
  } catch (e) {
    return 0.0
  }
}
export const selectCartId = (state: RootState) => {
  try {
    return state.cartItems.cartInfo?.id?.toString() ?? ''
  } catch (e) {
    return ''
  }
}
export const selectAllowedPaymentMethods = (state: RootState) => {
  try {
    return state.cartItems.cartInfo?.allowed_payment_methods as Record<string, any> || {}
  } catch (e) {
    return {}
  }
}
export const selectSafeCartItems = (state: RootState) => {
  try {
    return state.cartItems.cartItems.map((item) => ensureItemSafety(item))
  } catch (e) {
    return []
  }
}

export const {
  initializeCartItems,
  addItemLocally,
  removeItemLocally,
  updateItemQuantityLocally,
  recalculatePricesImmediately,
  clearCartLocally,
  clearCartItemsImmediately,
  resetCartForRefetch,
  stopClearingCart,
  showAddingItemLoading,
  hideAddingItemLoading,
  updateCartDataFromApi,
  updateCartDataFromCs,
} = cartItemsSlice.actions

export default cartItemsSlice.reducer